import React from "react";
import { Link } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaShieldAlt, 
  FaLock, 
  FaEye, 
  FaUserShield,
  FaServer,
  FaEnvelope,
  FaCalendarAlt,
  FaGavel
} from "react-icons/fa";
import "../styles/PrivacyPolicy.css";

function PrivacyPolicy() {
  return (
    <div className="privacy-container">
      <header className="privacy-header">
        <div className="container">
          <Link to="/" className="back-link">
            <FaArrowLeft />
            <span>Back to Home</span>
          </Link>
          <div className="privacy-hero">
            <h1>Privacy Policy</h1>
            <p>
              This Privacy Policy explains how Triagz ("we", "us", or "our") collects, uses, and protects your information when you use our endpoint security research platform. Please read this policy carefully to understand our practices and your rights.
            </p>
            <div className="last-updated">
              <FaCalendarAlt />
              <span>Last updated: July 13, 2025</span>
            </div>
          </div>
        </div>
      </header>

      <main className="privacy-main">
        <div className="container">
          <div className="privacy-content">
            {/* Introduction */}
            <section className="privacy-section">
              <div className="section-header">
                <FaEye className="section-icon" />
                <h2>Introduction</h2>
              </div>
              <div className="section-content">
                <p>
                  Triagz is an advanced endpoint security research platform that enables users to interact with endpoint agents via natural language prompts. Our platform leverages Deepseek LLM for AI-powered analysis (until we transition to an in-house LLM). 
                </p>
                <p>
                  This policy describes what information we collect, how we use it, how we protect it, and your rights regarding your data. By using Triagz, you consent to the practices described here.
                </p>
              </div>
            </section>

            {/* 1. Information We Collect */}
            <section className="privacy-section">
              <div className="section-header">
                <FaLock className="section-icon" />
                <h2>1. Information We Collect</h2>
              </div>
              <div className="section-content">
                <h3>A. Account and Profile Information</h3>
                <p>
                  When you create a Triagz account, we collect your name, email address, and profile details.
                </p>
                <h3>B. Usage and Technical Data</h3>
                <p>
                  We collect technical information about your use of the platform, such as IP address, browser type, device information, and usage logs. This helps us improve the platform and ensure security.
                </p>
                <h3>C. Prompt and Chat Data</h3>
                <p>
                  When you interact with the platform (e.g., by sending prompts like "list all running processes"), we store the prompt, the resulting chat history, and any metadata or content specifically requested in your prompt. <strong>We do not send or store entire files on our server; only file metadata or specific content you explicitly request is transmitted and processed.</strong>
                </p>
                <h3>D. Endpoint Agent Data</h3>
                <p>
                  The agent runs on your system and only communicates with the server as needed to fulfill your requests. All communication is handled over a secure channel. No files are uploaded to the server unless you explicitly request file content in your prompt.
                </p>
                <h3>E. Cookies & Similar Technologies</h3>
                <p>
                  We use cookies and similar technologies to operate and improve the platform, remember your preferences, and enhance your experience. You can control cookies through your browser settings.
                </p>
                <h3>F. Information We Do Not Collect</h3>
                <p>
                  We do not knowingly collect sensitive personal information (such as health, biometric, or financial data) or information from children under 18. If we learn that such data has been collected, we will promptly delete it.
                </p>
              </div>
            </section>

            {/* 2. How We Use Information */}
            <section className="privacy-section">
              <div className="section-header">
                <FaUserShield className="section-icon" />
                <h2>2. How We Use Information</h2>
              </div>
              <div className="section-content">
                <ul className="privacy-list">
                  <li><strong>To provide and maintain the service:</strong> Including account management, profile and chat history, and endpoint agent communication.</li>
                  <li><strong>To process your prompts and requests:</strong> Including sending relevant metadata or requested content to Deepseek LLM for analysis.</li>
                  <li><strong>To improve and secure the platform:</strong> Analyzing usage patterns, debugging, and enhancing security.</li>
                  <li><strong>To communicate with you:</strong> Sending updates, security alerts, and support messages.</li>
                  <li><strong>To comply with legal obligations:</strong> As required by law, regulation, or court order.</li>
                </ul>
                <p>
                  <strong>Note:</strong> We do not use your prompts, chat histories, or metadata to train our own models. Deepseek LLM may process your data for analysis, but we do not share your data with third parties for advertising or marketing purposes.
                </p>
              </div>
            </section>

            {/* 3. How We Share Information */}
            <section className="privacy-section">
              <div className="section-header">
                <FaServer className="section-icon" />
                <h2>3. How We Share Information</h2>
              </div>
              <div className="section-content">
                <ul className="privacy-list">
                  <li><strong>Service Providers:</strong> We may share data with trusted service providers (such as Deepseek LLM) to operate and improve the platform. These providers are contractually obligated to protect your data.</li>
                  <li><strong>Legal Compliance:</strong> We may disclose information if required by law, regulation, or legal process.</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale, your information may be transferred as part of the transaction.</li>
                  <li><strong>With Your Consent:</strong> We may share information with third parties if you give us explicit permission.</li>
                </ul>
                <p>
                  <strong>We do not sell your personal information or share it for advertising purposes.</strong>
                </p>
              </div>
            </section>

            {/* 4. Data Security */}
            <section className="privacy-section">
              <div className="section-header">
                <FaShieldAlt className="section-icon" />
                <h2>4. Data Security</h2>
              </div>
              <div className="section-content">
                <p>
                  We implement industry-standard security measures to protect your data, including encryption in transit, secure authentication, and regular security reviews. Communication between endpoint agents and the server is using a secured channel with appropriate safeguards. However, no method of transmission or storage is 100% secure; please use caution when sharing sensitive information.
                </p>
                <h3>Deepseek LLM Processing</h3>
                <p>
                  Until we deploy our own in-house LLM, prompts and requested content may be processed by Deepseek LLM. We require all providers to maintain strict confidentiality and security standards.
                </p>
              </div>
            </section>

            {/* 5. Data Retention */}
            <section className="privacy-section">
              <div className="section-header">
                <FaCalendarAlt className="section-icon" />
                <h2>5. Data Retention</h2>
              </div>
              <div className="section-content">
                <p>
                  We retain your account, and chat data only as long as necessary to provide the service, comply with legal obligations, resolve disputes, and enforce our agreements. When data is no longer needed, we securely delete or anonymize it.
                </p>
              </div>
            </section>

            {/* 6. Your Rights and Choices */}
            <section className="privacy-section">
              <div className="section-header">
                <FaGavel className="section-icon" />
                <h2>6. Your Rights and Choices</h2>
              </div>
              <div className="section-content">
                <p>
                  Depending on your location, you may have rights to access, correct, delete, or restrict the use of your personal data. You may also have the right to object to certain processing or request a copy of your data. To exercise these rights, please contact us using the information below. We will respond to all requests in accordance with applicable laws.
                </p>
              </div>
            </section>

            {/* 7. International Data Transfers */}
            <section className="privacy-section">
              <div className="section-header">
                <FaServer className="section-icon" />
                <h2>7. International Data Transfers</h2>
              </div>
              <div className="section-content">
                <p>
                  Triagz operates globally. Your data may be processed and stored in countries outside your own, but we always apply the same high standards of data protection regardless of location.
                </p>
              </div>
            </section>

            {/* 8. Policy Updates */}
            <section className="privacy-section">
              <div className="section-header">
                <FaCalendarAlt className="section-icon" />
                <h2>8. Policy Updates</h2>
              </div>
              <div className="section-content">
                <p>
                  We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top of this page. Your continued use of Triagz after any changes constitutes acceptance of the revised policy.
                </p>
              </div>
            </section>

            {/* 9. Contact Information */}
            <section className="privacy-section">
              <div className="section-header">
                <FaEnvelope className="section-icon" />
                <h2>9. Contact Us</h2>
              </div>
              <div className="section-content">
                <p>
                  If you have any questions, concerns, or requests regarding this Privacy Policy or your data, please contact us:
                </p>
                <div className="contact-info">
                  <div className="contact-item">
                    <strong>Email:</strong> admin@triagz.com
                  </div>
                  <div className="contact-item">
                    <strong>Response Time:</strong> We aim to respond to all privacy inquiries within 30 days
                  </div>
                </div>
              </div>
            </section>

          </div>
        </div>
      </main>
    </div>
  );
}

export default PrivacyPolicy; 